import { useCallback, useMemo } from 'react';
import {
  type EligibilityCheckResponse,
  useGetEncounterEligibilityQuery,
  useGetPatientEligibilityQuery,
  useRunEligibilityCheckMutation,
  useRunPatientEligibilityCheckMutation,
} from '@features/patient-coverages';
import { safeEligibility } from '@features/patient-coverages/utils/patientCost';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import type { BlockReadiness } from '../types/readiness';
import {
  type EligibilityCheckState,
  getEligibilityCheckState,
} from '../utils/eligibilityCheckState';

interface UseEligibilityBadgeStateOptions {
  patientId?: string;
  encounterId?: string;
  appointmentId?: string;
  serviceDate?: string;
}

export interface UseEligibilityBadgeStateResult {
  /** Gate disabled or tutorial mode — badge should render nothing. */
  isHidden: boolean;
  /** Convergent verdict from the BE (verified/collect/.../info) or null. */
  readiness: BlockReadiness | null;
  /** BE-supplied verbose label for the verdict ("Collect $30", "Verify with payer", …). */
  readinessLabel: string | null;
  /** Patient has insurance on file. Drives the "Add insurance" CTA when false. */
  hasCoverage: boolean;
  /** ISO timestamp of the auto-pinned appointment, for inline pin-context framing. */
  pinnedAppointmentAt: string | null;
  /**
   * The visit's date of service falls in a billable-coverage gap (GDN-2411):
   * insurance is on file but no plan window covers the visit date. Drives the
   * amber "Coverage gap" badge treatment on schedule surfaces.
   */
  visitCoverageGap: { visit_date: string; start: string; end?: string | null } | null;
  /** Full eligibility snapshot — popover/drawer compose against this. */
  eligibilityData: EligibilityCheckResponse | undefined;
  /** Semantic BE state for whether this surface should suggest running a check. */
  eligibilityCheckState: EligibilityCheckState;
  isLoading: boolean;
  /** Trigger an eligibility check. No-ops if neither patientId nor encounterId is set. */
  runCheck: () => void;
  isRunning: boolean;
  canRunCheck: boolean;
}

/**
 * Single-source state machine for the eligibility badge. Derives purely from
 * the BE's convergent contract — `readiness` + `has_coverage` — instead of the
 * legacy GREEN/YELLOW/RED + organized-coverages mapping.
 *
 * Encounter-scoped query is preferred when `encounterId` is supplied; otherwise
 * falls back to the patient-scoped endpoint. The BE auto-pins to the most
 * relevant encounter, so the FE has zero branching logic for which visit a
 * "Collect $30 · tomorrow 2pm" verdict refers to.
 */
export function useEligibilityBadgeState({
  patientId,
  encounterId,
  appointmentId,
  serviceDate,
}: UseEligibilityBadgeStateOptions): UseEligibilityBadgeStateResult {
  const isEnabled = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);
  const isHidden = !isEnabled;

  const { data: encounterEligibilityData, isLoading: isEncounterEligibilityLoading } =
    useGetEncounterEligibilityQuery(
      { encounterId: encounterId ?? '' },
      { skip: isHidden || !encounterId },
    );

  const { data: patientEligibilityData, isLoading: isPatientEligibilityLoading } =
    useGetPatientEligibilityQuery(
      { patientId: patientId ?? '' },
      { skip: isHidden || !!encounterId || !patientId },
    );

  const eligibilityData = useMemo(
    () =>
      safeEligibility(encounterEligibilityData ?? patientEligibilityData, {
        encounter_id: encounterId,
        appointment_id: appointmentId,
        service_date: serviceDate,
      }),
    [encounterEligibilityData, patientEligibilityData, encounterId, appointmentId, serviceDate],
  );

  const [triggerEncounterCheck, { isLoading: isEncounterRunning }] =
    useRunEligibilityCheckMutation();
  const [triggerPatientCheck, { isLoading: isPatientRunning }] =
    useRunPatientEligibilityCheckMutation();

  const runCheck = useCallback(() => {
    if (encounterId) {
      triggerEncounterCheck({ encounterId });
    } else if (patientId) {
      triggerPatientCheck({ patientId });
    }
  }, [encounterId, patientId, triggerEncounterCheck, triggerPatientCheck]);

  const isLoading =
    !isHidden &&
    ((!!encounterId && isEncounterEligibilityLoading) ||
      (!encounterId && !!patientId && isPatientEligibilityLoading));

  return {
    isHidden,
    readiness: eligibilityData?.readiness ?? null,
    readinessLabel: eligibilityData?.readiness_label ?? null,
    hasCoverage: eligibilityData?.has_coverage ?? false,
    pinnedAppointmentAt: eligibilityData?.pinned_appointment_at ?? null,
    visitCoverageGap: eligibilityData?.visit_coverage_gap ?? null,
    eligibilityData,
    eligibilityCheckState: getEligibilityCheckState(eligibilityData),
    isLoading,
    runCheck,
    isRunning: isEncounterRunning || isPatientRunning,
    canRunCheck: !!encounterId || !!patientId,
  };
}
